import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin, AlertCircle, X } from "lucide-react";

/**
 * AllAddressesMapDialog — modal with a full Leaflet map showing every
 * address of a customer.
 *
 * Geocodes each address once via Nominatim (countrycodes=gr), then
 * drops a numbered marker per resolved address and auto-fits the
 * viewport to enclose all of them. Already-geocoded queries hit the
 * shared cache so reopening the dialog after editing is instant.
 *
 * Why Leaflet?  The previous «mini preview» used OSM's embed.html
 * iframe which only supports a single marker. For an N-address
 * overview we need real markers + a fitBounds call — Leaflet is the
 * lightest free option that ships with OSM tile support out of the
 * box. ~140KB gzipped, no API key.
 */

// Shared cache: same Map instance survives across all dialog open/close
// cycles. Filled progressively as the operator browses customers.
const geoCache = new Map();

async function geocode(query) {
  if (!query) return null;
  if (geoCache.has(query)) return geoCache.get(query);
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&countrycodes=gr&limit=1&q=${encodeURIComponent(query)}`,
      { headers: { Accept: "application/json" } },
    );
    if (!r.ok) return null;
    const data = await r.json();
    if (!Array.isArray(data) || data.length === 0) {
      geoCache.set(query, null);
      return null;
    }
    const { lat, lon } = data[0];
    const fLat = parseFloat(lat);
    const fLon = parseFloat(lon);
    if (!Number.isFinite(fLat) || !Number.isFinite(fLon)) {
      geoCache.set(query, null);
      return null;
    }
    const res = { lat: fLat, lon: fLon };
    geoCache.set(query, res);
    return res;
  } catch {
    return null;
  }
}

export default function AllAddressesMapDialog({ open, onClose, customerName, addresses = [] }) {
  // Filter out tombstoned addresses ahead of time so the markers and
  // the sidebar list stay aligned.
  const visible = (addresses || []).filter((a) => a && a.status !== "deleted");
  const [resolved, setResolved] = useState([]); // [{addr, lat, lon, query, idx}]
  const [progress, setProgress] = useState({ done: 0, total: 0, failed: 0 });
  const mapRef = useRef(null);
  const containerRef = useRef(null);

  // 1) Geocode every visible address (one-at-a-time to respect OSM 1 req/s).
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const queries = visible.map((a) => ({
      addr: a,
      query: [a.street, a.street_no, a.area].map((s) => (s || "").trim()).filter(Boolean).join(" "),
    }));
    setProgress({ done: 0, total: queries.length, failed: 0 });
    setResolved([]);
    if (queries.length === 0) return undefined;

    (async () => {
      for (let i = 0; i < queries.length; i += 1) {
        if (!alive) return;
        const q = queries[i];
        if (!q.query || q.query.length < 4) {
          setProgress((p) => ({ ...p, done: p.done + 1, failed: p.failed + 1 }));
          continue;
        }
        const r = await geocode(q.query);
        if (!alive) return;
        if (r) {
          setResolved((prev) => [...prev, { addr: q.addr, ...r, query: q.query, idx: i + 1 }]);
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        } else {
          setProgress((p) => ({ ...p, done: p.done + 1, failed: p.failed + 1 }));
        }
        // Polite throttle for fresh (uncached) calls.
        if (!geoCache.has(q.query)) {
          await new Promise((res) => setTimeout(res, 1100));
        }
      }
    })();

    return () => { alive = false; };
  }, [open]);

  // 2) Once we have at least one resolved point, build / refresh the Leaflet map.
  useEffect(() => {
    if (!open || resolved.length === 0 || !containerRef.current) return undefined;
    let cancelled = false;

    (async () => {
      const L = (await import("leaflet")).default;
      // Inject Leaflet's CSS once.
      if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css";
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }
      if (cancelled || !containerRef.current) return;

      // (Re)create the map. Cheaper than diff-updating markers each render.
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch { /* noop */ }
        mapRef.current = null;
      }
      const map = L.map(containerRef.current, { zoomControl: true });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap',
      }).addTo(map);
      mapRef.current = map;

      // Drop a numbered marker per resolved address.
      const bounds = [];
      resolved.forEach((p) => {
        const icon = L.divIcon({
          className: "addr-pin",
          html: `<div style="
            width:28px;height:28px;border-radius:50% 50% 50% 0;
            background:${p.addr.is_default ? "#2563eb" : "#dc2626"};
            border:2px solid #fff;
            box-shadow:0 2px 6px rgba(0,0,0,0.35);
            transform:rotate(-45deg);
            display:flex;align-items:center;justify-content:center;
            color:#fff;font-weight:900;font-size:11px;font-family:system-ui;
          "><span style="transform:rotate(45deg)">${p.idx}</span></div>`,
          iconSize: [28, 28],
          iconAnchor: [14, 28],
        });
        const m = L.marker([p.lat, p.lon], { icon }).addTo(map);
        const street = `${p.addr.street || ""}${p.addr.street_no ? " " + p.addr.street_no : ""}`.trim();
        m.bindPopup(`<b>#${p.idx} ${esc(street || "—")}</b><br/>${esc(p.addr.area || "")}${p.addr.instructions ? "<br/><i>" + esc(p.addr.instructions) + "</i>" : ""}`);
        bounds.push([p.lat, p.lon]);
      });

      if (bounds.length === 1) {
        map.setView(bounds[0], 16);
      } else {
        map.fitBounds(bounds, { padding: [40, 40] });
      }
    })();

    return () => {
      cancelled = true;
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch { /* noop */ }
        mapRef.current = null;
      }
    };
  }, [open, resolved]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl w-[92vw] h-[88vh] p-0 gap-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <MapPin size={18} /> Διευθύνσεις στον χάρτη — {customerName || "—"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {progress.total === 0
              ? "Δεν υπάρχουν διευθύνσεις."
              : `Επεξεργασία ${progress.done}/${progress.total} διευθύνσεων${progress.failed ? ` (${progress.failed} δεν βρέθηκαν)` : ""}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex overflow-hidden">
          {/* Left sidebar — numbered address list */}
          <div className="w-64 shrink-0 border-r border-slate-200 overflow-y-auto bg-slate-50 p-2 space-y-1.5" data-testid="all-addr-sidebar">
            {visible.length === 0 && (
              <div className="text-xs text-slate-400 italic text-center py-4">Καμία διεύθυνση</div>
            )}
            {visible.map((a, i) => {
              const idx = i + 1;
              const found = resolved.find((r) => r.idx === idx);
              return (
                <div
                  key={i}
                  className={`text-xs rounded border-2 p-2 ${
                    found
                      ? a.is_default ? "bg-blue-50 border-blue-300" : "bg-white border-slate-200"
                      : "bg-amber-50 border-amber-200"
                  }`}
                  data-testid={`all-addr-row-${idx}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white font-bold text-[10px] ${
                      a.is_default ? "bg-blue-600" : found ? "bg-red-600" : "bg-amber-500"
                    }`}>
                      {idx}
                    </span>
                    <span className="font-bold truncate">
                      {a.street || "—"}{a.street_no ? ` ${a.street_no}` : ""}
                    </span>
                  </div>
                  <div className="text-slate-500 text-[10px] pl-6.5 ml-0.5 mt-0.5 truncate">{a.area || "—"}</div>
                  {!found && progress.done >= idx && (
                    <div className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1">
                      <AlertCircle size={10} /> Δεν βρέθηκε στον χάρτη
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Right pane — map (or progress/empty state) */}
          <div className="flex-1 relative bg-slate-100" data-testid="all-addr-map">
            {progress.total === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center text-slate-400 italic">
                Δεν υπάρχουν διευθύνσεις προς εμφάνιση.
              </div>
            ) : progress.done < progress.total && resolved.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center text-slate-500 gap-2">
                <Loader2 size={16} className="animate-spin" /> Φόρτωση χάρτη...
              </div>
            ) : resolved.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center text-slate-400 italic gap-1">
                <AlertCircle size={14} /> Καμία διεύθυνση δεν εντοπίστηκε.
              </div>
            ) : null}
            <div ref={containerRef} className="w-full h-full" data-testid="all-addr-leaflet-canvas" />
          </div>
        </div>

        <DialogFooter className="px-4 py-2.5 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" onClick={onClose} data-testid="all-addr-close">
            <X size={14} className="mr-1.5" /> Κλείσιμο
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function esc(s) {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
